'use client';
import s from '@app/[lang]/products/product-buy/product-buy.module.css';
import { useBundler } from '@app/components/bundler/bundler-provider/bundler-provider';
import BundlerBreadcrumbs from '@app/components/bundler/BundlerBreadcrumbs';
import BundlerLoaderProductInfo from '@app/components/bundler/BundlerLoaderProductInfo';
import Linker from '@app/components/linker/linker';
import { Language } from '@app/lib/store-context/Context';
import {
    getDefaultVariant,
    getStructuredVariants,
} from '@containers/Product/ssgHelpers';
import VariantAvailability from '@containers/Product/VariantAvailability';
import VariantOptionList from '@containers/Product/VariantOptionList';
import cn from 'clsx';
import { useParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import styles from './BundlerProductInfo.module.css';

const BundlerProductInfo = () => {
    const { lang } = useParams();
    const {
        chosenBundleVariants,
        selectedProduct,
        addVariantToBundle,
        isFetching,
    } = useBundler();

    let chosenVariantForProduct = null;

    Object.keys(chosenBundleVariants).forEach((key) => {
        if (
            selectedProduct &&
            chosenBundleVariants[key].product.handle === selectedProduct.handle
        ) {
            chosenVariantForProduct = chosenBundleVariants[key];
        }
    });

    const defaultVariant = getDefaultVariant(
        selectedProduct?.variants?.edges,
        chosenVariantForProduct
    );
    const structuredVariants = getStructuredVariants(
        selectedProduct?.variants?.edges
    );
    const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
    const [readMore, setReadMore] = useState(false);

    useEffect(() => {
        if (selectedProduct) {
            const defaultVariant = getDefaultVariant(
                selectedProduct.variants.edges,
                chosenVariantForProduct
            );
            setSelectedVariant(defaultVariant);
        }
    }, [selectedProduct]);

    if (
        !selectedProduct ||
        typeof selectedProduct === 'undefined' ||
        isFetching
    )
        return <BundlerLoaderProductInfo />;

    const onSelectVariant = (size) => {
        const selectedVariantData = structuredVariants.find(
            (variant) => variant.size === size
        );

        setSelectedVariant(selectedVariantData.variant);
    };

    const hasOptions = !!(
        structuredVariants?.length && Object.keys(structuredVariants[0]).length
    );

    let selections: null | { size: string } = null;

    if (hasOptions && selectedVariant) {
        const sizeOption = selectedVariant.selectedOptions.find(
            (option) => option.name.toLowerCase() === 'size'
        );
        selections = { size: sizeOption.value };
    }

    const variantStockQuantity = (variant: any = null) =>
        variant
            ? variant?.quantityAvailable
            : selectedVariant?.quantityAvailable;

    const unavailableOptions = (key) =>
        structuredVariants
            ?.map((option) => (!option.available ? option[key] : null))
            ?.filter((e) => e);
    const availableStock = variantStockQuantity();

    let variantAvailable =
        selectedVariant && selectedVariant.availableForSale && availableStock;
    let numberOfInstancesInBundle = 0;

    if (chosenBundleVariants) {
        numberOfInstancesInBundle = Object.values(chosenBundleVariants)?.filter(
            // @ts-ignore
            (variant) => variant!.id === selectedVariant?.id
        ).length;
    }

    if (numberOfInstancesInBundle >= availableStock) {
        variantAvailable = false;
    }

    const shortDescription = selectedProduct?.metafield?.value;
    const readLess = shortDescription?.split('</br>')[1];
    const linkText = readMore ? '< Read Less' : 'Read More >';
    const dots = !readMore ? '...' : '';
    const { handle } = selectedProduct;
    const productTags = defaultVariant.product.tags;

    const comparedPrice = selectedVariant?.compareAtPriceV2?.amount || 0.0;
    const price = parseFloat(selectedVariant?.priceV2?.amount);
    const compareAtPrice =
        parseFloat(comparedPrice) > 0 && parseFloat(comparedPrice) !== price
            ? parseFloat(comparedPrice)
            : null;

    const priceFormatter = new Intl.NumberFormat(lang, {
        style: 'currency',
        currency: defaultVariant?.priceV2?.currencyCode,
        currencyDisplay: 'narrowSymbol',
    });
    const currencyCode = selectedVariant?.priceV2?.currencyCode;
    return (
        <>
            <div className={styles.bpiHeader}>
                <BundlerBreadcrumbs />
            </div>
            <div className={styles.bundlerProductInfo}>
                {shortDescription && (
                    <div className={styles.bpiDescription}>
                        {shortDescription?.split('</br>')[0]}
                        {dots}
                        {readMore && readLess}
                        <a
                            className={styles.bpiDescriptionLink}
                            onClick={() => {
                                setReadMore(!readMore);
                            }}
                        >
                            {linkText}
                        </a>
                    </div>
                )}
                <div className={styles.bpiDesktopLink}>
                    <Linker
                        title={`Go to ${selectedProduct.title}`}
                        href={`/products/${handle}`}
                        target="_blank"
                        className={styles.bpiLink}
                        lang={lang as Language}
                    >
                        View product
                    </Linker>
                </div>
                <div className={styles.bpiDivider}></div>
                <div className={styles.bpiPrice}>
                    <div
                        className={cn(
                            s.priceContainer,
                            s.spacer,
                            'mmds-component-one-detail'
                        )}
                    >
                        {compareAtPrice && (
                            <div className={s.compareAtPrice}>
                                {priceFormatter.format(compareAtPrice)}{' '}
                                {currencyCode}
                            </div>
                        )}
                        {price && (
                            <div className={s.price}>
                                {priceFormatter.format(price)} {currencyCode}
                            </div>
                        )}
                    </div>
                    <div className={styles.bpiMobileLink}>
                        <Linker
                            title={`Go to ${selectedProduct.title}`}
                            href={`/products/${handle}`}
                            target="_blank"
                            className={styles.bpiLink}
                            lang={lang as Language}
                        >
                            View product
                        </Linker>
                    </div>
                </div>
                <div className="bpi-variant">
                    <VariantOptionList
                        optionName="size"
                        variantOptions={structuredVariants} // todo: need to add structuredVariants
                        selectedOption={selections ? selections.size : null}
                        unavailableOptions={unavailableOptions('size')}
                        callback={onSelectVariant}
                        preventUnavailableSelection
                    />
                    <VariantAvailability
                        variant={selectedVariant}
                        availableQuantity={variantStockQuantity()}
                        quantityFloor={2}
                        tags={productTags}
                    />
                    <br />
                </div>
                <div className={styles.bpiButton}>
                    <button
                        disabled={!variantAvailable}
                        type="button"
                        onClick={() => addVariantToBundle(selectedVariant)}
                    >
                        SELECT
                    </button>
                </div>
            </div>
        </>
    );
};

export default BundlerProductInfo;
